<template>
  <v-dialog :value="value" width="600" persistent>
    <v-card>
      <AdCardTitle dialog>
          {{ modalTitle }}
      </AdCardTitle>

      <v-card-text>
        <v-form ref="form" v-model="modal.formIsValid">
          <v-row dense>
            <!-- Code -->
            <v-col cols="12">
              <EditableString
                v-model="modal.inputs.code"
                :label="$t('code')"
                required
                :disabled="!!selected"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <!-- Display name -->
            <v-col cols="12">
              <EditableString
                v-model="modal.inputs.displayName"
                :label="$t('display-name')"
                required
              />
            </v-col>
          </v-row>
          <v-row v-if="codeSystem" dense>
            <!-- CodeSystem -->
            <v-col cols="12">
              <v-combobox
                ref="codeSystem"
                class="required"
                :rules="codeSystemComboboxRules"
                :value="modal.inputs.codeSystem"
                @input="handleCodeSystemInput"
                :label="$t('codesystem')"
                :items="codeSystemComboboxItems"
                :loading="$store.state.terminology.loading.codeSystemItems"
                item-value="uuid"
                :filter="codeSystemSearchFilter"
                clearable
                @keydown.enter="blurCodeSystemCombobox"
              >
                <template #item="{ item }">
                  <AdStatusDot larger tooltip class="mr-3" :status="item.statusCode" icon="mdi-format-list-bulleted-type" />
                  {{ item.displayName || item.name }} {{(item.versionLabel)}}
                </template>
                <template #selection="{ item }">
                  {{ item.id }}
                  <v-chip v-if="item.displayName || item.name" class="ml-2" small outlined>
                    <AdStatusDot class="mr-2" tooltip :status="item.statusCode" icon="mdi-format-list-bulleted-type" />
                    {{ item.displayName || item.name }} {{(item.versionLabel)}}
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
          </v-row>
          <v-row dense>
            <!-- Canonical URI -->
            <v-col cols="12">
              <EditableString
                v-model="modal.inputs.canonicalUri"
                :label="$t('canonicalUri')"
                :required="canonicalUriRequired"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <AdButton type="cancel" @click="closeModal"/>
        <AdButton :type="selected ? 'save' : 'add'" :disabled="!modal.formIsValid" @click="save" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'

const initialModal = {
  formIsValid: false,
  inputs: {
    canonicalUri: '',
    code: '',
    displayName: '',
    codeSystem: ''
  }
}
export default {
  name: 'EditUnitOptions',

  props: {
    // Open/close state of the modal
    value: {
      type: Boolean,
      required: false,
      default: false
    },
    // The unit options selected
    selected: {
      type: Object,
      required: false,
      default: null
    },
    title: {
      type: String
    },
    canonicalUriRequired: {
      type: Boolean,
      default: true
    },
    codeSystem: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      modal: cloneDeep(initialModal)
    }
  },
  computed: {
    codeSystemComboboxItems () {
      const csItems = this.$store.getters['terminology/codeSystemItems'] || []
      return [
        { header: this.$t('select-codesystem-or-enter-oid') },
        ...csItems
      ]
    },
    codeSystemComboboxRules () {
      return [
        v => !!v || this.$t('required-field'),
        v => /^[0-2](\.(0|[1-9][0-9]*))*$/.test(v?.id) || this.$t('invalid-input')
      ]
    },
    modalTitle () {
      if (this.title) return this.title

      const op = this.selected ? 'edit' : 'add'
      return this.$t(`${op}-unit-option`)
    }
  },
  methods: {
    blurCodeSystemCombobox () {
      this.$refs.codeSystem.blur()
    },
    closeModal () {
      this.modal = cloneDeep(initialModal)
      this.$emit('input', false)
    },
    codeSystemSearchFilter (item, queryText) {
      const { displayName, name, id } = item
      return [displayName, name, id]
        .map(text => text?.toLowerCase())
        .some(text => text?.includes(queryText.toLowerCase()))
    },
    handleCodeSystemInput (value) {
      if (typeof value === 'string') {
        this.modal.inputs.codeSystem = {
          id: value
        }
      } else if (typeof value === 'object') {
        this.modal.inputs.codeSystem = value
      }
    },
    save () {
      const newUnitOption = cloneDeep(this.modal.inputs)
      if (this.codeSystem && typeof newUnitOption.codeSystem?.id === 'string') {
        newUnitOption.codeSystem = newUnitOption.codeSystem.id
      } else {
        delete newUnitOption.codeSystem
      }

      this.$emit('add', newUnitOption)
      this.closeModal()
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler (modalIsOpen) {
        if (!modalIsOpen) {
          return
        }
        if (this.$refs.form) {
          this.$refs.form.reset()
        }

        // fetch codesystems if not loaded yet
        if (this.codeSystem && !this.$store.state.terminology.loaded.codeSystemItems) {
          await this.$store.dispatch('terminology/getCodeSystemList', { prefix: this.projectInFocus?.prefix })
            .catch(error => {
              const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
              this.$oops(errorMessage)
            })
        }

        if (this.selected !== null) {
          const inputs = cloneDeep(this.selected)
          if (inputs.codeSystem) {
            const codeSystem = this.$store.getters['terminology/codeSystemItems']?.find(cs => cs.id === inputs.codeSystem)
            inputs.codeSystem = codeSystem || { id: inputs.codeSystem }
          }
          this.modal.inputs = inputs
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .required .v-label:after {
  content: "*";
  font-weight: bold;
  color: var(--v-error-base);
}
</style>
